/**
 * Live topic progress timers.
 * Topic clock starts only after startCurrentTopic(); pause/Q&A freeze topic ticks.
 * Session duration timer is wall-clock and never pauses.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { SessionTopicPlan } from "./types";
import {
  computeProjectedSessionEndTime,
  computeScheduledEndTime,
  plannedSessionMinutes,
} from "./sessionEndTime";
import {
  formatSessionElapsed,
  formatTopicElapsedLabel,
  inferOpenSubtopicStartIso,
  isTopicTeachingActive,
  laterStartIso,
} from "./topicSegments";
import {
  applyCompletedFlags,
  loadCompletedTopicIds,
  markTopicCompletedLocal,
} from "./normalizeTopicPlan";
import {
  loadLiveTiming,
  saveLiveTiming,
  sanitizePersistedTopicsTiming,
  type LiveTimingPersisted,
} from "./timingPersistence";
import type { LiveSessionClockMessage } from "../liveSessionClockSync";
import { useServerClockOffsetMs } from "../../utils/serverTimeIst";

/** Elapsed seconds from a stored subtopic/topic start ISO → now. */
function elapsedSecFromStartIso(
  startIso: string | null | undefined,
  nowMs: number = Date.now(),
): number | null {
  if (!startIso) return null;
  const startMs = Date.parse(startIso);
  if (!Number.isFinite(startMs) || startMs > nowMs) return null;
  return Math.max(0, Math.floor((nowMs - startMs) / 1000));
}

export type UseLiveTopicProgressArgs = {
  sessionId: string;
  role: "trainer" | "student";
  topics: SessionTopicPlan[];
  /** Session actual start (or scheduled if not live yet) — display baseline. */
  startTime: string;
  /** Original scheduled start — Scheduled End only. Falls back to startTime. */
  scheduledStartTime?: string;
  /** ISO/actual start timestamp — anchors the never-pausing session timer. */
  sessionStartedAtIso?: string | null;
  durationMinutes: number;
  /** When false, topic clocks pause (e.g. Q&A). Session timer ignores this. */
  running?: boolean;
  pauseAccumulatedSec?: number;
  /** Remaining seconds on the active Q&A pause window (0 when not paused). */
  qaRemainingSec?: number;
  syncedClock?: LiveSessionClockMessage | null;
};

function resolveSessionAnchorMs(
  sessionStartedAtIso: string | null | undefined,
  persisted: LiveTimingPersisted | null | undefined,
): number {
  if (sessionStartedAtIso) {
    const parsed = Date.parse(sessionStartedAtIso);
    if (Number.isFinite(parsed)) return parsed;
  }
  if (persisted?.sessionAnchorMs && persisted.sessionAnchorMs > 0) {
    return persisted.sessionAnchorMs;
  }
  if (persisted && persisted.updatedAt > 0) {
    return (
      persisted.updatedAt - Math.max(0, persisted.sessionElapsedSec || 0) * 1000
    );
  }
  return Date.now();
}

function mergeTimingOntoTopics(
  topics: SessionTopicPlan[],
  timing: LiveTimingPersisted["topicsTiming"],
): SessionTopicPlan[] {
  return topics.map((t) => {
    const row = timing[t.id];
    if (!row) return t;
    const ended =
      Boolean(row.actualEndAt) ||
      row.actualDurationSec != null ||
      Boolean(row.skipped);
    let subtopics = t.subtopics;
    if (t.subtopics?.length && row.subtopics) {
      subtopics = t.subtopics.map((sub) => {
        const subRow = row.subtopics?.[sub.id];
        if (!subRow) return sub;
        const subEnded =
          Boolean(subRow.actualEndAt) ||
          subRow.actualDurationSec != null ||
          Boolean(subRow.skipped);
        return {
          ...sub,
          actualStartAt: subRow.actualStartAt || sub.actualStartAt,
          actualEndAt: subRow.actualEndAt || sub.actualEndAt,
          actualDurationSec:
            subRow.actualDurationSec != null
              ? subRow.actualDurationSec
              : sub.actualDurationSec,
          completed: subEnded || sub.completed,
          skipped:
            "skipped" in subRow ? Boolean(subRow.skipped) : sub.skipped,
        };
      });
    }
    return {
      ...t,
      actualStartAt: row.actualStartAt || t.actualStartAt,
      actualEndAt: row.actualEndAt || t.actualEndAt,
      actualDurationSec:
        row.actualDurationSec != null
          ? row.actualDurationSec
          : t.actualDurationSec,
      completed: ended || t.completed,
      skipped: "skipped" in row ? Boolean(row.skipped) : t.skipped,
      ...(subtopics ? { subtopics } : {}),
    };
  });
}

function mergeSubtopicLists(
  planSubs: NonNullable<SessionTopicPlan["subtopics"]> | undefined,
  prevSubs: NonNullable<SessionTopicPlan["subtopics"]> | undefined,
): SessionTopicPlan["subtopics"] | undefined {
  if (!planSubs?.length) return planSubs;
  if (!prevSubs?.length) return planSubs;
  const prevById = new Map(prevSubs.map((s) => [s.id, s]));
  return planSubs.map((sub) => {
    const old = prevById.get(sub.id);
    if (!old) return sub;
    return {
      ...sub,
      actualStartAt: sub.actualStartAt || old.actualStartAt,
      actualEndAt: sub.actualEndAt || old.actualEndAt,
      actualDurationSec:
        sub.actualDurationSec != null
          ? sub.actualDurationSec
          : old.actualDurationSec,
      completed: sub.completed || old.completed,
      skipped: sub.skipped ?? old.skipped,
    };
  });
}

function firstOpenSubtopicIndex(
  subs: NonNullable<SessionTopicPlan["subtopics"]>,
): number {
  const idx = subs.findIndex((s) => !s.completed && !s.skipped);
  return idx >= 0 ? idx : Math.max(0, subs.length - 1);
}

export function useLiveTopicProgress({
  sessionId,
  role,
  topics: initialTopics,
  startTime,
  scheduledStartTime,
  sessionStartedAtIso = null,
  durationMinutes,
  running = true,
  pauseAccumulatedSec = 0,
  qaRemainingSec = 0,
  syncedClock = null,
}: UseLiveTopicProgressArgs) {
  const serverOffsetMs = useServerClockOffsetMs();
  const persisted = useMemo(() => loadLiveTiming(sessionId), [sessionId]);
  const sessionAnchorMsRef = useRef(
    resolveSessionAnchorMs(sessionStartedAtIso, persisted),
  );

  const [topics, setTopics] = useState<SessionTopicPlan[]>(() => {
    const timing = sanitizePersistedTopicsTiming(
      persisted?.topicsTiming || {},
      initialTopics,
    );
    return mergeTimingOntoTopics(
      applyCompletedFlags(initialTopics, loadCompletedTopicIds(sessionId)),
      timing,
    );
  });
  const [sessionElapsedSec, setSessionElapsedSec] = useState(() =>
    Math.max(
      0,
      Math.floor((Date.now() - sessionAnchorMsRef.current) / 1000),
    ),
  );
  const [topicElapsedSec, setTopicElapsedSec] = useState(
    () => persisted?.topicElapsedSec ?? 0,
  );
  const [topicTimeBonusSec, setTopicTimeBonusSec] = useState(
    () => persisted?.topicTimeBonusSec ?? 0,
  );
  const [topicStarted, setTopicStarted] = useState(() => {
    const timing = sanitizePersistedTopicsTiming(
      persisted?.topicsTiming || {},
      initialTopics,
    );
    const merged = mergeTimingOntoTopics(
      applyCompletedFlags(initialTopics, loadCompletedTopicIds(sessionId)),
      timing,
    );
    const current =
      merged.find((t) => !t.completed && !t.skipped) ??
      merged[merged.length - 1];
    return isTopicTeachingActive(current);
  });
  const [pauseTick, setPauseTick] = useState(0);
  const topicsTimingRef = useRef<LiveTimingPersisted["topicsTiming"]>(
    sanitizePersistedTopicsTiming(persisted?.topicsTiming || {}, initialTopics),
  );
  const lastTopicIdRef = useRef<string | null>(
    persisted?.currentTopicId ?? null,
  );

  // Sync plan identity without wiping topic elapsed unless current topic changes.
  useEffect(() => {
    topicsTimingRef.current = sanitizePersistedTopicsTiming(
      topicsTimingRef.current,
      initialTopics,
    );
    // Seed timing ref from API-hydrated plan (hard refresh restore).
    for (const t of initialTopics) {
      const prev = topicsTimingRef.current[t.id] || {};
      const subMap = { ...(prev.subtopics || {}) };
      for (const sub of t.subtopics || []) {
        if (
          !sub.actualStartAt &&
          !sub.actualEndAt &&
          sub.actualDurationSec == null &&
          sub.skipped == null
        ) {
          continue;
        }
        subMap[sub.id] = {
          ...(subMap[sub.id] || {}),
          actualStartAt: sub.actualStartAt || subMap[sub.id]?.actualStartAt,
          actualEndAt: sub.actualEndAt || subMap[sub.id]?.actualEndAt,
          actualDurationSec:
            sub.actualDurationSec != null
              ? sub.actualDurationSec
              : subMap[sub.id]?.actualDurationSec,
          skipped:
            sub.skipped !== undefined
              ? Boolean(sub.skipped)
              : subMap[sub.id]?.skipped,
        };
      }
      topicsTimingRef.current[t.id] = {
        ...prev,
        actualStartAt: t.actualStartAt || prev.actualStartAt,
        actualEndAt: t.actualEndAt || prev.actualEndAt,
        actualDurationSec:
          t.actualDurationSec != null
            ? t.actualDurationSec
            : prev.actualDurationSec,
        skipped: t.skipped !== undefined ? Boolean(t.skipped) : prev.skipped,
        ...(Object.keys(subMap).length ? { subtopics: subMap } : {}),
      };
    }
    const planById = new Map(initialTopics.map((t) => [t.id, t]));
    setTopics((prev) => {
      const next = mergeTimingOntoTopics(
        applyCompletedFlags(initialTopics, loadCompletedTopicIds(sessionId)),
        topicsTimingRef.current,
      );
      // Preserve actual* fields already on prev when plan refresh omits them
      const prevById = new Map(prev.map((t) => [t.id, t]));
      return next.map((t) => {
        const old = prevById.get(t.id);
        const plan = planById.get(t.id);
        if (!old) return t;
        return {
          ...t,
          actualStartAt:
            t.actualStartAt ??
            (plan?.actualStartAt ? old.actualStartAt : undefined),
          actualEndAt: t.actualEndAt || old.actualEndAt,
          actualDurationSec:
            t.actualDurationSec != null
              ? t.actualDurationSec
              : old.actualDurationSec,
          completed: t.completed || old.completed,
          // undefined keeps prior; explicit false/true from timing/plan wins.
          skipped: t.skipped ?? old.skipped,
          subtopics: mergeSubtopicLists(t.subtopics, old.subtopics),
        };
      });
    });
  }, [initialTopics, sessionId]);

  // Re-anchor when API actual start arrives (more accurate than join time).
  useEffect(() => {
    if (!sessionStartedAtIso) return;
    const parsed = Date.parse(sessionStartedAtIso);
    if (!Number.isFinite(parsed)) return;
    sessionAnchorMsRef.current = parsed;
    setSessionElapsedSec(
      Math.max(0, Math.floor((Date.now() - parsed) / 1000)),
    );
  }, [sessionStartedAtIso]);

  useEffect(() => {
    if (role !== "student" || !syncedClock) return;
    // Keep session wall-clock aligned with trainer if we drift, but never pause it.
    const trainerElapsed = Math.max(0, syncedClock.sessionElapsedSec || 0);
    const localElapsed = Math.max(
      0,
      Math.floor((Date.now() - sessionAnchorMsRef.current) / 1000),
    );
    if (Math.abs(trainerElapsed - localElapsed) > 3) {
      sessionAnchorMsRef.current = Date.now() - trainerElapsed * 1000;
      setSessionElapsedSec(trainerElapsed);
    }
    setTopicElapsedSec(syncedClock.topicElapsedSec);
    setTopicTimeBonusSec(syncedClock.topicTimeBonusSec);
    const durations = syncedClock.topicActualDurationsSec || {};
    const completedIds = new Set(
      Array.isArray(syncedClock.completedTopicIds)
        ? syncedClock.completedTopicIds.map(String)
        : [],
    );
    const skippedIds = new Set(
      Array.isArray(syncedClock.skippedTopicIds)
        ? syncedClock.skippedTopicIds.map(String)
        : [],
    );
    setTopics((prev) =>
      applyCompletedFlags(prev, syncedClock.completedTopicIds).map((t) => {
        const parentDone = completedIds.has(t.id) || skippedIds.has(t.id);
        const parentSkipped = parentDone ? skippedIds.has(t.id) : Boolean(t.skipped);
        const parentSec = durations[t.id];
        // Ignore bogus published 0 for completed (trainer UI may use start→end).
        const nextParentDuration =
          parentSec == null
            ? t.actualDurationSec
            : parentSec > 0 || parentSkipped
              ? parentSec
              : t.actualDurationSec != null && t.actualDurationSec > 0
                ? t.actualDurationSec
                : undefined;
        const currentSubId = syncedClock.currentSubtopicId
          ? String(syncedClock.currentSubtopicId)
          : null;
        const subElapsed = Math.max(0, syncedClock.subtopicElapsedSec || 0);
        const publishedStart =
          typeof syncedClock.currentSubtopicActualStartAt === "string" &&
          syncedClock.currentSubtopicActualStartAt.trim()
            ? syncedClock.currentSubtopicActualStartAt.trim()
            : null;
        return {
          ...t,
          skipped: parentSkipped,
          completed: t.completed || parentDone,
          actualDurationSec: nextParentDuration,
          subtopics: t.subtopics?.map((s) => {
            const subDone = completedIds.has(s.id) || skippedIds.has(s.id);
            const subSkipped = subDone ? skippedIds.has(s.id) : Boolean(s.skipped);
            const subSec = durations[s.id];
            let actualStartAt = s.actualStartAt;
            let actualEndAt = s.actualEndAt;
            let actualDurationSec =
              subSec == null
                ? s.actualDurationSec
                : subSec > 0 || subSkipped
                  ? subSec
                  : s.actualDurationSec != null && s.actualDurationSec > 0
                    ? s.actualDurationSec
                    : undefined;
            if (subDone && actualDurationSec != null && actualDurationSec > 0) {
              const implied =
                actualStartAt && actualEndAt
                  ? Math.floor(
                      (Date.parse(actualEndAt) - Date.parse(actualStartAt)) /
                        1000,
                    )
                  : null;
              if (!actualEndAt || implied == null || implied < actualDurationSec) {
                actualEndAt = actualEndAt || new Date().toISOString();
                actualStartAt = new Date(
                  Date.parse(actualEndAt) - actualDurationSec * 1000,
                ).toISOString();
              }
            }
            if (
              !subDone &&
              currentSubId &&
              s.id === currentSubId &&
              Boolean(syncedClock.topicStarted)
            ) {
              if (publishedStart) {
                actualStartAt = publishedStart;
              } else if (!actualStartAt) {
                actualStartAt =
                  inferOpenSubtopicStartIso(t, s.id) ||
                  (subElapsed > 0
                    ? new Date(Date.now() - subElapsed * 1000).toISOString()
                    : new Date().toISOString());
              } else if (actualStartAt) {
                const startMs = Date.parse(actualStartAt);
                if (Number.isFinite(startMs)) {
                  const localElapsed = Math.max(
                    0,
                    Math.floor((Date.now() - startMs) / 1000),
                  );
                  if (subElapsed > localElapsed + 2) {
                    actualStartAt = new Date(
                      Date.now() - subElapsed * 1000,
                    ).toISOString();
                  }
                }
              }
            }
            return {
              ...s,
              skipped: subSkipped,
              completed: s.completed || subDone,
              actualStartAt,
              actualEndAt,
              actualDurationSec,
            };
          }),
        };
      }),
    );
  }, [role, syncedClock]);

  const currentTopic = useMemo(
    () =>
      topics.find((t) => !t.completed && !t.skipped) ??
      topics[topics.length - 1],
    [topics],
  );

  // Trainer: topic started only after explicit Start (backend actual_start_at).
  useEffect(() => {
    if (role !== "trainer") return;
    const active = isTopicTeachingActive(currentTopic);
    setTopicStarted((prev) => (prev === active ? prev : active));
  }, [
    role,
    currentTopic?.id,
    currentTopic?.actualStartAt,
    currentTopic?.actualEndAt,
    currentTopic?.completed,
    currentTopic?.skipped,
  ]);

  // Reset topic clock when current topic id changes (after complete/skip).
  useEffect(() => {
    const id = currentTopic?.id ?? null;
    if (id && lastTopicIdRef.current && lastTopicIdRef.current !== id) {
      setTopicElapsedSec(0);
      setTopicTimeBonusSec(0);
      setTopicStarted(false);
    }
    lastTopicIdRef.current = id;
  }, [currentTopic?.id]);

  const topicClockRunning =
    running && topicStarted && !(role === "student" && syncedClock);

  // Total session duration — wall clock; never pauses for Q&A / topic / disconnect.
  useEffect(() => {
    const tick = () => {
      setSessionElapsedSec(
        Math.max(
          0,
          Math.floor((Date.now() - sessionAnchorMsRef.current) / 1000),
        ),
      );
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [sessionId]);

  // Topic teaching clock only (can pause).
  useEffect(() => {
    if (!topicClockRunning || role !== "trainer") return;
    const id = window.setInterval(() => {
      setTopicElapsedSec((s) => s + 1);
    }, 1000);
    return () => window.clearInterval(id);
  }, [topicClockRunning, role]);

  useEffect(() => {
    // Expected End is now-based; keep it live every second for trainer
    // (topic delay, Q&A remaining, overrun all need a wall-clock tick).
    if (role !== "trainer") {
      const paused =
        !running ||
        qaRemainingSec > 0 ||
        pauseAccumulatedSec > 0 ||
        Boolean(syncedClock?.qaPaused);
      if (!paused) return;
    }
    const id = window.setInterval(() => setPauseTick((t) => t + 1), 1000);
    return () => window.clearInterval(id);
  }, [
    role,
    pauseAccumulatedSec,
    qaRemainingSec,
    running,
    syncedClock?.qaPaused,
  ]);

  // Persist trainer timing state
  useEffect(() => {
    if (role !== "trainer") return;
    saveLiveTiming(sessionId, {
      sessionElapsedSec,
      sessionAnchorMs: sessionAnchorMsRef.current,
      topicElapsedSec,
      topicTimeBonusSec,
      pauseAccumulatedSec,
      topicStarted,
      currentTopicId: currentTopic?.id ?? null,
      topicsTiming: topicsTimingRef.current,
      updatedAt: Date.now(),
    });
  }, [
    role,
    sessionId,
    sessionElapsedSec,
    topicElapsedSec,
    topicTimeBonusSec,
    pauseAccumulatedSec,
    topicStarted,
    currentTopic?.id,
    topics,
  ]);

  const effectiveBonusSec =
    role === "student" && syncedClock
      ? syncedClock.topicTimeBonusSec
      : topicTimeBonusSec;

  const effectivePauseSec =
    role === "student" && syncedClock
      ? syncedClock.pauseAccumulatedSec
      : pauseAccumulatedSec;

  const topicTimeLimitSec =
    (currentTopic?.durationMin ?? 15) * 60 + effectiveBonusSec;

  const studentTopicElapsedSec = useMemo(() => {
    if (syncedClock) return syncedClock.topicElapsedSec;
    return Math.max(0, topicElapsedSec);
  }, [topicElapsedSec, syncedClock]);

  const elapsedForProjection =
    role === "trainer" ? topicElapsedSec : studentTopicElapsedSec;

  const topicProgressPercent = useMemo(() => {
    if (topics.length === 0) return 0;
    if (topicTimeLimitSec <= 0) return 0;
    return Math.min(
      100,
      Math.round((elapsedForProjection / topicTimeLimitSec) * 100),
    );
  }, [topics.length, topicTimeLimitSec, elapsedForProjection]);

  const plannedMin = useMemo(() => {
    const sum = plannedSessionMinutes(topics);
    return sum > 0 ? sum : durationMinutes;
  }, [topics, durationMinutes]);

  const scheduleBase = scheduledStartTime || startTime;

  const scheduledEndTime = useMemo(
    () => computeScheduledEndTime(scheduleBase, plannedMin),
    [scheduleBase, plannedMin],
  );

  const projectedEndTime = useMemo(() => {
    void pauseTick;
    if (role === "student" && syncedClock?.projectedEndTime) {
      return syncedClock.projectedEndTime;
    }
    if (topics.length === 0 && qaRemainingSec <= 0) {
      return scheduledEndTime;
    }
    // Expected End = server-now + remaining topic work + Q&A remaining.
    // While paused, now advances and Q&A remaining shrinks; on resume it recalculates.
    return computeProjectedSessionEndTime(
      startTime,
      topics,
      currentTopic,
      elapsedForProjection,
      effectiveBonusSec,
      effectivePauseSec,
      topicStarted,
      qaRemainingSec,
      Date.now() + serverOffsetMs,
    );
  }, [
    topics,
    startTime,
    currentTopic,
    elapsedForProjection,
    effectiveBonusSec,
    effectivePauseSec,
    topicStarted,
    qaRemainingSec,
    scheduledEndTime,
    role,
    syncedClock,
    pauseTick,
    serverOffsetMs,
  ]);

  const startCurrentTopic = useCallback(() => {
    if (!currentTopic || currentTopic.completed) return null;
    const startedAt = new Date().toISOString();
    topicsTimingRef.current = {
      ...topicsTimingRef.current,
      [currentTopic.id]: {
        ...topicsTimingRef.current[currentTopic.id],
        actualStartAt:
          topicsTimingRef.current[currentTopic.id]?.actualStartAt || startedAt,
      },
    };
    setTopics((prev) =>
      prev.map((t) =>
        t.id === currentTopic.id
          ? { ...t, actualStartAt: t.actualStartAt || startedAt }
          : t,
      ),
    );
    setTopicStarted(true);
    if (topicElapsedSec === 0) {
      /* keep */
    }
    return startedAt;
  }, [currentTopic, topicElapsedSec]);

  const completeCurrentTopic = useCallback(
    (opts?: { skipped?: boolean }) => {
      if (!currentTopic || currentTopic.completed) return null;
      const endedAt = new Date().toISOString();
      const durationSec = Math.max(0, topicElapsedSec);
      markTopicCompletedLocal(sessionId, currentTopic.id);
      const skipped = Boolean(opts?.skipped);
      topicsTimingRef.current = {
        ...topicsTimingRef.current,
        [currentTopic.id]: {
          ...topicsTimingRef.current[currentTopic.id],
          actualStartAt:
            topicsTimingRef.current[currentTopic.id]?.actualStartAt ||
            currentTopic.actualStartAt,
          actualEndAt: endedAt,
          actualDurationSec: durationSec,
          skipped,
        },
      };
      setTopics((prev) =>
        prev.map((t) =>
          t.id === currentTopic.id
            ? {
                ...t,
                completed: true,
                skipped,
                actualEndAt: endedAt,
                actualDurationSec: durationSec,
              }
            : t,
        ),
      );
      setTopicElapsedSec(0);
      setTopicTimeBonusSec(0);
      setTopicStarted(false);
      return {
        topicId: currentTopic.id,
        actualEndAt: endedAt,
        actualDurationSec: durationSec,
        skipped,
        actualStartAt:
          topicsTimingRef.current[currentTopic.id]?.actualStartAt ||
          currentTopic.actualStartAt,
      };
    },
    [currentTopic, sessionId, topicElapsedSec],
  );

  const addMoreTime = useCallback((minutes = 10) => {
    setTopicTimeBonusSec((s) => s + minutes * 60);
  }, []);

  const addSubtopicMoreTime = useCallback((minutes = 10) => {
    setSubtopicTimeBonusSec((s) => s + minutes * 60);
  }, []);

  // --- Subtopic tracking ---
  const [subtopicIndex, setSubtopicIndex] = useState(0);
  const [subtopicElapsedSec, setSubtopicElapsedSec] = useState(0);
  const [subtopicStarted, setSubtopicStarted] = useState(false);
  const [subtopicTimeBonusSec, setSubtopicTimeBonusSec] = useState(0);

  // Student: mirror trainer subtopic cursor + elapsed from the clock channel.
  useEffect(() => {
    if (role !== "student" || !syncedClock) return;
    setSubtopicElapsedSec(Math.max(0, syncedClock.subtopicElapsedSec || 0));
    const subId = syncedClock.currentSubtopicId;
    if (!subId) {
      setSubtopicStarted(Boolean(syncedClock.topicStarted));
      return;
    }
    for (const t of topics) {
      const idx = t.subtopics?.findIndex((s) => s.id === subId) ?? -1;
      if (idx >= 0) {
        setSubtopicIndex(idx);
        setSubtopicStarted(Boolean(syncedClock.topicStarted));
        break;
      }
    }
  }, [role, syncedClock, topics]);

  const currentSubtopics = currentTopic?.subtopics ?? [];
  const currentSubtopic =
    currentSubtopics.length > 0 ? currentSubtopics[subtopicIndex] ?? null : null;
  const hasSubtopics = currentSubtopics.length > 0;
  const subtopicStartedAtRef = useRef<Record<string, string>>({});
  const lastSubtopicTopicIdRef = useRef<string | null>(null);
  /** Forces trainer Time taken to recompute from start each second. */
  const [subtopicWallTick, setSubtopicWallTick] = useState(0);

  const resolveCurrentSubtopicStartIso = useCallback(
    (subId?: string | null): string | undefined => {
      const id = subId || currentSubtopic?.id;
      if (!id) return undefined;
      const fromSub =
        currentSubtopic?.id === id ? currentSubtopic.actualStartAt : undefined;
      const fromTopic = currentTopic?.subtopics?.find((s) => s.id === id)
        ?.actualStartAt;
      const fromTiming =
        currentTopic?.id
          ? topicsTimingRef.current[currentTopic.id]?.subtopics?.[id]
              ?.actualStartAt
          : undefined;
      const raw =
        fromSub ||
        fromTopic ||
        subtopicStartedAtRef.current[id] ||
        fromTiming ||
        undefined;
      // No start yet (waiting for pre-quiz push/skip) → do not fall back to
      // parent topic start (that makes Time taken jump to whole-topic elapsed).
      if (!raw) return undefined;
      return laterStartIso(raw, currentTopic?.actualStartAt) || raw;
    },
    [currentSubtopic, currentTopic],
  );

  // Trainer Time taken is wall-clock from subtopic start (not a counter that can reset).
  // Student: same wall-clock tick once start ISO is synced from trainer.
  // Clock does not run until startCurrentSubtopic (after pre-quiz push/skip).
  useEffect(() => {
    if (!hasSubtopics || !running) return;
    const open =
      currentSubtopic &&
      !currentSubtopic.completed &&
      !currentSubtopic.skipped
        ? currentSubtopic
        : null;
    if (!open) return;
    const knownStart = resolveCurrentSubtopicStartIso(open.id);
    if (!knownStart) {
      setSubtopicStarted(false);
      setSubtopicElapsedSec(0);
      return;
    }
    if (role === "trainer") {
      // always tick for trainer open sub with a real start
    } else if (role === "student") {
      const startIso =
        open.actualStartAt ||
        (syncedClock?.currentSubtopicId === open.id
          ? syncedClock.currentSubtopicActualStartAt
          : null);
      if (!startIso) return;
    } else {
      return;
    }
    const tick = () => {
      const startIso =
        resolveCurrentSubtopicStartIso(open.id) ||
        open.actualStartAt ||
        (syncedClock?.currentSubtopicId === open.id
          ? syncedClock.currentSubtopicActualStartAt
          : undefined);
      const fromStart = elapsedSecFromStartIso(startIso ?? undefined);
      if (fromStart != null) {
        setSubtopicElapsedSec(fromStart);
        setSubtopicStarted(true);
      }
      setSubtopicWallTick((n) => n + 1);
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [
    role,
    hasSubtopics,
    currentSubtopic?.id,
    currentSubtopic?.completed,
    currentSubtopic?.skipped,
    currentSubtopic?.actualStartAt,
    syncedClock?.currentSubtopicId,
    syncedClock?.currentSubtopicActualStartAt,
    resolveCurrentSubtopicStartIso,
    running,
  ]);

  // Restore / advance subtopic cursor from completed flags (survives hard refresh).
  useEffect(() => {
    const topicId = currentTopic?.id ?? null;
    const subs = currentTopic?.subtopics;
    if (!topicId || !subs?.length) {
      lastSubtopicTopicIdRef.current = topicId;
      setSubtopicIndex(0);
      setSubtopicElapsedSec(0);
      setSubtopicStarted(false);
      setSubtopicTimeBonusSec(0);
      return;
    }

    const topicChanged = lastSubtopicTopicIdRef.current !== topicId;
    lastSubtopicTopicIdRef.current = topicId;

    const openIdx = firstOpenSubtopicIndex(subs);
    setSubtopicIndex((prev) => {
      if (topicChanged) return openIdx;
      const cur = subs[prev];
      if (cur && !cur.completed && !cur.skipped) return prev;
      return openIdx;
    });

    if (isTopicTeachingActive(currentTopic)) {
      const open = subs[openIdx];
      if (!open) return;
      // Only resume a clock that already started (pre-quiz push/skip or no-pre start).
      // Do not invent start from previous sub end / topic start — that makes
      // Manage Topic / post-quiz appear as soon as the next sub is selected.
      const recoveredStart =
        open.actualStartAt ||
        topicsTimingRef.current[topicId]?.subtopics?.[open.id]?.actualStartAt ||
        subtopicStartedAtRef.current[open.id] ||
        undefined;
      if (!recoveredStart) {
        setSubtopicStarted(false);
        setSubtopicElapsedSec(0);
        if (topicChanged) setSubtopicTimeBonusSec(0);
        return;
      }
      const startIso =
        laterStartIso(recoveredStart, currentTopic.actualStartAt) ||
        recoveredStart;
      setSubtopicStarted(true);
      subtopicStartedAtRef.current[open.id] = startIso;
      const fromStart = elapsedSecFromStartIso(startIso);
      if (fromStart != null) setSubtopicElapsedSec(fromStart);
      const prevTiming = topicsTimingRef.current[topicId] || {};
      topicsTimingRef.current = {
        ...topicsTimingRef.current,
        [topicId]: {
          ...prevTiming,
          subtopics: {
            ...(prevTiming.subtopics || {}),
            [open.id]: {
              ...(prevTiming.subtopics?.[open.id] || {}),
              actualStartAt: laterStartIso(
                prevTiming.subtopics?.[open.id]?.actualStartAt,
                startIso,
              ),
            },
          },
        },
      };
      if (!open.actualStartAt || (open.actualStartAt && startIso !== open.actualStartAt)) {
        setTopics((prev) =>
          prev.map((t) => {
            if (t.id !== topicId || !t.subtopics) return t;
            return {
              ...t,
              subtopics: t.subtopics.map((s) =>
                s.id === open.id
                  ? {
                      ...s,
                      actualStartAt: laterStartIso(s.actualStartAt, startIso),
                    }
                  : s,
              ),
            };
          }),
        );
      }
    } else if (topicChanged) {
      setSubtopicStarted(false);
      setSubtopicElapsedSec(0);
      setSubtopicTimeBonusSec(0);
    }
  }, [
    currentTopic?.id,
    currentTopic?.actualStartAt,
    currentTopic?.actualEndAt,
    currentTopic?.completed,
    currentTopic?.skipped,
    currentTopic?.subtopics
      ?.map(
        (s) =>
          `${s.id}:${s.completed ? 1 : 0}:${s.skipped ? 1 : 0}:${s.actualDurationSec ?? ""}:${s.actualStartAt ?? ""}:${s.actualEndAt ?? ""}`,
      )
      .join("|"),
  ]);

  // Heal out-of-order subtopic flags: if a later sub is Done, earlier ones must be Done too.
  // Otherwise the progress bar keeps the first incomplete as "Ongoing" forever.
  useEffect(() => {
    if (role !== "trainer") return;
    const topicId = currentTopic?.id;
    const subs = currentTopic?.subtopics;
    if (!topicId || !subs?.length) return;

    let maxDone = -1;
    for (let i = 0; i < subs.length; i++) {
      if (subs[i].completed || subs[i].skipped) maxDone = i;
    }
    if (maxDone < 0) return;

    const toHeal = subs
      .map((s, i) => ({ s, i }))
      .filter(({ s, i }) => i < maxDone && !s.completed && !s.skipped);
    if (toHeal.length === 0) return;

    const endedAt = new Date().toISOString();
    const healIds = new Set(toHeal.map(({ s }) => s.id));

    setTopics((prev) =>
      prev.map((t) => {
        if (t.id !== topicId || !t.subtopics) return t;
        return {
          ...t,
          subtopics: t.subtopics.map((s, i) => {
            if (!healIds.has(s.id)) return s;
            const start =
              s.actualStartAt ||
              topicsTimingRef.current[topicId]?.subtopics?.[s.id]?.actualStartAt ||
              endedAt;
            const startMs = Date.parse(start);
            const endMs = Date.parse(endedAt);
            const durationSec =
              s.actualDurationSec != null
                ? s.actualDurationSec
                : Number.isFinite(startMs) && Number.isFinite(endMs) && endMs >= startMs
                  ? Math.floor((endMs - startMs) / 1000)
                  : 0;
            return {
              ...s,
              completed: true,
              actualStartAt: start,
              actualEndAt: s.actualEndAt || endedAt,
              actualDurationSec: durationSec,
            };
          }),
        };
      }),
    );

    const prevTiming = topicsTimingRef.current[topicId] || {};
    const nextSubs = { ...(prevTiming.subtopics || {}) };
    for (const { s } of toHeal) {
      const existing = nextSubs[s.id] || {};
      const start =
        existing.actualStartAt ||
        s.actualStartAt ||
        endedAt;
      nextSubs[s.id] = {
        ...existing,
        actualStartAt: start,
        actualEndAt: existing.actualEndAt || endedAt,
        actualDurationSec:
          existing.actualDurationSec != null
            ? existing.actualDurationSec
            : s.actualDurationSec,
      };
      markTopicCompletedLocal(sessionId, s.id);
    }
    topicsTimingRef.current = {
      ...topicsTimingRef.current,
      [topicId]: { ...prevTiming, subtopics: nextSubs },
    };

    const openIdx = subs.findIndex(
      (s, i) => i > maxDone && !s.completed && !s.skipped,
    );
    if (openIdx >= 0) {
      setSubtopicIndex(openIdx);
    } else {
      const anyOpen = firstOpenSubtopicIndex(
        subs.map((s, i) =>
          i < maxDone ? { ...s, completed: true } : s,
        ),
      );
      setSubtopicIndex(anyOpen);
    }
  }, [
    role,
    sessionId,
    currentTopic?.id,
    currentTopic?.subtopics
      ?.map((s) => `${s.id}:${s.completed ? 1 : 0}:${s.skipped ? 1 : 0}`)
      .join("|"),
  ]);

  const subtopicTimeLimitSec = currentSubtopic
    ? currentSubtopic.durationMin * 60 + subtopicTimeBonusSec
    : 0;

  // Trainer: always prefer start→now when this subtopic was already started.
  // Student: same rule once trainer start ISO (or backfilled start) is on the subtopic.
  const effectiveSubtopicElapsed = useMemo(() => {
    void subtopicWallTick;
    const open =
      currentSubtopic &&
      !currentSubtopic.completed &&
      !currentSubtopic.skipped
        ? currentSubtopic
        : null;
    if (open) {
      const startIso =
        resolveCurrentSubtopicStartIso(open.id) ||
        (role === "student" &&
        syncedClock?.currentSubtopicId === open.id &&
        syncedClock.currentSubtopicActualStartAt
          ? syncedClock.currentSubtopicActualStartAt
          : undefined);
      const fromStart = elapsedSecFromStartIso(startIso);
      if (fromStart != null) return fromStart;
    }
    if (role === "student" && syncedClock) {
      return Math.max(0, syncedClock.subtopicElapsedSec || 0);
    }
    return subtopicElapsedSec;
  }, [
    role,
    syncedClock,
    subtopicWallTick,
    currentSubtopic,
    subtopicElapsedSec,
    resolveCurrentSubtopicStartIso,
  ]);

  const startCurrentSubtopic = useCallback(
    (subtopicId?: string, opts?: { forceNow?: boolean }) => {
      if (!currentTopic?.subtopics?.length) return null;
      const target =
        (subtopicId
          ? currentTopic.subtopics.find((s) => s.id === subtopicId)
          : null) ||
        currentTopic.subtopics[subtopicIndex] ||
        currentTopic.subtopics.find((s) => !s.completed && !s.skipped) ||
        null;
      if (!target) return null;
      const forceNow = Boolean(opts?.forceNow);
      // Keep the first start timestamp; never reset mid-subtopic — unless
      // forceNow (pre-quiz push/skip) which is the real teaching start.
      const existingStart = forceNow
        ? undefined
        : laterStartIso(
            laterStartIso(
              target.actualStartAt,
              topicsTimingRef.current[currentTopic.id]?.subtopics?.[target.id]
                ?.actualStartAt,
            ),
            subtopicStartedAtRef.current[target.id],
          );
      // Fresh subtopic (no prior start) must begin at "now".
      const iso = existingStart
        ? laterStartIso(existingStart, currentTopic.actualStartAt) ||
          existingStart
        : new Date().toISOString();
      subtopicStartedAtRef.current[target.id] = iso;
      const targetIndex = currentTopic.subtopics.findIndex(
        (s) => s.id === target.id,
      );
      if (targetIndex >= 0) setSubtopicIndex(targetIndex);
      const prevTiming = topicsTimingRef.current[currentTopic.id] || {};
      topicsTimingRef.current = {
        ...topicsTimingRef.current,
        [currentTopic.id]: {
          ...prevTiming,
          subtopics: {
            ...(prevTiming.subtopics || {}),
            [target.id]: {
              ...(prevTiming.subtopics?.[target.id] || {}),
              actualStartAt: iso,
            },
          },
        },
      };
      setTopics((prev) =>
        prev.map((t) => {
          if (t.id !== currentTopic.id || !t.subtopics) return t;
          return {
            ...t,
            subtopics: t.subtopics.map((s) =>
              s.id === target.id
                ? {
                    ...s,
                    actualStartAt: forceNow
                      ? iso
                      : laterStartIso(s.actualStartAt, iso) || iso,
                  }
                : s,
            ),
          };
        }),
      );
      setSubtopicStarted(true);
      const fromStart = elapsedSecFromStartIso(iso);
      setSubtopicElapsedSec(fromStart ?? 0);
      setSubtopicTimeBonusSec(0);
      setSubtopicWallTick((n) => n + 1);
      return iso;
    },
    [currentTopic, subtopicIndex],
  );

  const completeCurrentSubtopic = useCallback(
    (opts?: { skipped?: boolean }) => {
      if (!currentSubtopic || !currentTopic) return null;
      const skipped = Boolean(opts?.skipped);
      const endedAt = new Date().toISOString();
      let startedAt =
        subtopicStartedAtRef.current[currentSubtopic.id] ||
        currentSubtopic.actualStartAt ||
        undefined;
      // Always persist a start timestamp so Time taken can use start→end.
      if (!startedAt) {
        const tickSec = Math.max(0, subtopicElapsedSec);
        startedAt = new Date(
          Date.parse(endedAt) - tickSec * 1000,
        ).toISOString();
      }
      let durationSec = 0;
      if (!skipped) {
        const startMs = Date.parse(startedAt);
        const endMs = Date.parse(endedAt);
        if (
          Number.isFinite(startMs) &&
          Number.isFinite(endMs) &&
          endMs >= startMs
        ) {
          durationSec = Math.floor((endMs - startMs) / 1000);
        } else {
          durationSec = Math.max(0, subtopicElapsedSec);
        }
      }

      const prevTiming = topicsTimingRef.current[currentTopic.id] || {};
      topicsTimingRef.current = {
        ...topicsTimingRef.current,
        [currentTopic.id]: {
          ...prevTiming,
          subtopics: {
            ...(prevTiming.subtopics || {}),
            [currentSubtopic.id]: {
              actualStartAt: startedAt,
              actualEndAt: endedAt,
              actualDurationSec: durationSec,
              skipped,
            },
          },
        },
      };

      setTopics((prev) =>
        prev.map((t) => {
          if (t.id !== currentTopic.id || !t.subtopics) return t;
          return {
            ...t,
            subtopics: t.subtopics.map((s) =>
              s.id === currentSubtopic.id
                ? {
                    ...s,
                    completed: true,
                    skipped,
                    actualStartAt: startedAt,
                    actualEndAt: endedAt,
                    actualDurationSec: durationSec,
                  }
                : s,
            ),
          };
        }),
      );
      markTopicCompletedLocal(sessionId, currentSubtopic.id);

      const nextIndex = subtopicIndex + 1;
      const isLast = nextIndex >= currentSubtopics.length;

      const nextSub = !isLast ? currentSubtopics[nextIndex] : null;
      // Do not stamp next.actualStartAt here — teaching (and the clock) start
      // only after pre-quiz push/skip, or immediately when there is no pre-quiz.
      if (nextSub) {
        delete subtopicStartedAtRef.current[nextSub.id];
        const timing = topicsTimingRef.current[currentTopic.id] || {};
        const prevSubTiming = timing.subtopics?.[nextSub.id];
        if (prevSubTiming?.actualStartAt) {
          const cleaned = { ...prevSubTiming };
          delete cleaned.actualStartAt;
          topicsTimingRef.current = {
            ...topicsTimingRef.current,
            [currentTopic.id]: {
              ...timing,
              subtopics: {
                ...(timing.subtopics || {}),
                [nextSub.id]: cleaned,
              },
            },
          };
        }
      }
      setSubtopicElapsedSec(0);
      setSubtopicStarted(false);
      setSubtopicTimeBonusSec(0);

      const payload = {
        subtopicId: currentSubtopic.id,
        subtopicName: currentSubtopic.name,
        durationSec,
        actualStartAt: startedAt,
        actualEndAt: endedAt,
        plannedDurationMin: currentSubtopic.durationMin,
        skipped,
      };

      if (isLast) {
        setSubtopicIndex(0);
        return {
          ...payload,
          autoCompleteTopic: true as const,
        };
      }

      setSubtopicIndex(nextIndex);
      return {
        ...payload,
        autoCompleteTopic: false as const,
      };
    },
    [currentSubtopic, currentTopic, sessionId, subtopicElapsedSec, subtopicIndex, currentSubtopics.length],
  );

  const effectiveElapsed =
    role === "trainer" ? topicElapsedSec : studentTopicElapsedSec;
  const topicTimeLimitReached =
    Boolean(currentTopic) &&
    topicStarted &&
    !currentTopic?.completed &&
    topicTimeLimitSec > 0 &&
    effectiveElapsed >= topicTimeLimitSec;

  return {
    topics,
    currentTopic,
    topicStarted:
      role === "student" && syncedClock
        ? Boolean(syncedClock.topicStarted)
        : topicStarted,
    topicProgressPercent,
    topicElapsedSec: effectiveElapsed,
    topicElapsedLabel: formatTopicElapsedLabel(effectiveElapsed),
    topicTimeLimitSec,
    topicTimeLimitReached,
    topicTimeBonusSec: effectiveBonusSec,
    sessionElapsedSec,
    sessionTimerLabel: formatSessionElapsed(sessionElapsedSec),
    scheduledEndTime,
    projectedEndTime,
    plannedSessionMinutes: plannedMin,
    startCurrentTopic,
    completeCurrentTopic,
    addMoreTime,
    addSubtopicMoreTime,
    getTopicsTiming: () => topicsTimingRef.current,
    // Subtopic tracking
    hasSubtopics,
    currentSubtopic,
    subtopicIndex,
    subtopicElapsedSec: effectiveSubtopicElapsed,
    subtopicElapsedLabel: formatTopicElapsedLabel(effectiveSubtopicElapsed),
    subtopicTimeLimitSec,
    subtopicStarted,
    subtopicProgressPercent: subtopicTimeLimitSec > 0
      ? Math.min(
          100,
          Math.round((effectiveSubtopicElapsed / subtopicTimeLimitSec) * 100),
        )
      : 0,
    subtopicTimeLimitReached:
      hasSubtopics &&
      subtopicStarted &&
      Boolean(currentSubtopic) &&
      !currentSubtopic?.completed &&
      subtopicTimeLimitSec > 0 &&
      effectiveSubtopicElapsed >= subtopicTimeLimitSec,
    startCurrentSubtopic,
    completeCurrentSubtopic,
  };
}
